import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  getFirestore,
  query,
  updateDoc,
  where,
} from "firebase/firestore/lite";
import {
  EIntegrationPlatform,
  TIntegration,
} from "../models/integrations.model";
import { TResponse } from "./apiWorker";

export async function getIntegrations({
  onSuccess,
  onError,
}: {
  onSuccess?: (data: TResponse<TIntegration>) => void;
  onError?: (data: any) => void;
}) {
  const db = getFirestore();
  const uid = localStorage.getItem("uid");

  const queried = query(
    collection(db, "integrations"),
    where("userId", "==", uid)
  );

  const { docs } = await getDocs(queried);

  const data = docs.map((doc) => {
    return {
      ...doc.data(),
      id: doc.id,
    } as TIntegration;
  });

  onSuccess?.({
    content: data,
    total: data.length,
    limit: 0,
    offset: 0,
  });
}

export async function createIntegration({
  onSuccess,
  onError,
  accessToken,
  platform,
  refreshExpireAt,
  refreshToken,
}: {
  accessToken: string;
  platform: EIntegrationPlatform;
  refreshToken: string;
  refreshExpireAt: string;
  onSuccess?: (data: TIntegration) => void;
  onError?: (data: any) => void;
}) {
  const db = getFirestore();
  const uid = localStorage.getItem("uid") || "";

  const queried = query(
    collection(db, "integrations"),
    where("userId", "==", uid),
    where("platform", "==", platform)
  );

  const { docs } = await getDocs(queried);

  const firstDoc = docs[0];

  if (firstDoc && firstDoc.exists()) {
    const data = firstDoc.data() as TIntegration;

    updateDoc(doc(db, "integrations", firstDoc.id), {
      accessToken,
      refreshToken,
      refreshExpireAt,
      lastCheckAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    })
      .then(() => {
        onSuccess?.({
          ...data,
          accessToken,
          refreshToken,
          refreshExpireAt,
          lastCheckAt: new Date().toISOString(),
        });
      })
      .catch((error) => {
        onError?.(error);
      });

    return;
  }

  const docData = {
    accessToken,
    platform,
    userId: uid,

    metadataPublic: {},
    refreshToken,
    refreshExpireAt,

    lastCheckAt: new Date().toISOString(),

    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };

  const docRef = collection(db, "integrations");
  addDoc(docRef, docData)
    .then((doc) => {
      onSuccess?.({
        ...docData,
        id: doc.id,
      });
    })
    .catch((error) => {
      onError?.(error);
    });
}

export async function updateIntegration({
  onSuccess,
  onError,
  integrationId,
  accessToken,
  refreshExpireAt,
  metadataPublic,
}: {
  integrationId: string;
  accessToken: string;
  refreshExpireAt: string;
  metadataPublic: any;
  onSuccess?: (data: TIntegration) => void;
  onError?: (data: any) => void;
}) {
  const db = getFirestore();

  const docRef = doc(db, "integrations", integrationId);

  const _doc = await getDoc(docRef);

  if (!_doc.exists()) {
    return onError?.("Integration not found");
  }

  const updatedIntegration = {
    ..._doc.data(),

    accessToken,
    refreshExpireAt,
    metadataPublic,
    id: _doc.id,
  };

  updateDoc(docRef, updatedIntegration)
    .then(() => {
      onSuccess?.(updatedIntegration as any);
    })
    .catch((error) => {
      onError?.(error);
    });
}

export async function deleteIntegration({
  onSuccess,
  onError,
  platform,
}: {
  platform: EIntegrationPlatform;
  onSuccess?: (data: TIntegration) => void;
  onError?: (data: any) => void;
}) {
  const db = getFirestore();
  const uid = localStorage.getItem("uid") || "";

  const queried = query(
    collection(db, "integrations"),
    where("userId", "==", uid),
    where("platform", "==", platform)
  );

  const { docs } = await getDocs(queried);

  const firstDoc = docs[0];

  if (!firstDoc || !firstDoc.exists()) {
    onError?.("Integration not found");
    return;
  }

  deleteDoc(doc(db, "integrations", firstDoc.id))
    .then(() => {
      onSuccess?.(firstDoc.data() as TIntegration);
    })
    .catch((error) => {
      onError?.(error);
    });
}
